import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import { useSession } from '../session';
import { Button } from '../ui/Button';
import { EmptyState } from '../ui/EmptyState';
import {
  ErrorState,
  OfflineState,
  shownState,
  refreshFailed,
  StaleNotice,
} from '../ui/QueryStates';
import { Skeleton } from '../ui/Skeleton';
import { IconInventory } from '../icons';
import { VanPointsSheet } from './VanPointsSheet';
import { VanTemplateSheet } from './VanTemplateSheet';
import { UnderlineTabs, UnderlineTabStrip, UnderlineTabPanel } from '../ui/Tabs';

export function VanTemplatesTab({
  onManageLocations,
}: {
  onManageLocations?: () => void;
}) {
  const trpc = useTRPC(),
    session = useSession();
  const vans = useQuery(trpc.restocks.vans.queryOptions());
  const [selected, setSelected] = useState<string | null>(null);
  const [tab, setTab] = useState('vans');
  const state = shownState(vans, vans.data);
  if (state === 'offline') return <OfflineState icon={IconInventory} what="vans" />;
  if (state === 'loading') return <Skeleton className="h-40" />;
  if (state === 'error')
    return (
      <ErrorState icon={IconInventory} what="vans" onRetry={() => void vans.refetch()} />
    );
  return (
    <UnderlineTabs value={tab} onValueChange={setTab}>
      <UnderlineTabStrip
        tabs={[
          { key: 'templates', label: 'Templates' },
          { key: 'vans', label: 'Vans' },
        ]}
      />
      <UnderlineTabPanel value="templates">
        <TemplateCatalogue />
      </UnderlineTabPanel>
      <UnderlineTabPanel value="vans">
        <div className="space-y-4">
          <h2 className="font-heading text-lg font-semibold">Vans</h2>
          <StaleNotice
            show={refreshFailed(vans, vans.data)}
            onRetry={() => void vans.refetch()}
          />
          {!vans.data?.length ? (
            <EmptyState
              icon={IconInventory}
              title="No vans set up"
              description="Assign a van to a current team member in Stock locations."
              action={
                onManageLocations ? (
                  <Button className="min-h-11" onClick={onManageLocations}>
                    Go to Stock
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <ul className="divide-y divide-border">
              {vans.data.map((v) => (
                <li key={v.vanId} className="py-1">
                  <button
                    className="min-h-11 w-full space-y-1 py-3 text-left hover:bg-surface-2"
                    onClick={() => setSelected(v.vanId)}
                    disabled={!v.ownerAvailable}
                  >
                    <span className="block break-words text-sm font-semibold">
                      {v.name}
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      {v.ownerAvailable
                        ? v.ownerName
                        : 'Assign a current team member in Stock'}{' '}
                      · {v.watchedCount} watched
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      Template: {v.templateName ?? 'None'}
                    </span>
                    <span className="block text-xs">
                      {v.needsFeeder ? 'Choose feeder' : v.feederName}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {selected ? (
            <VanPointsSheet
              key={`${session.orgId}:${session.userId}:${selected}`}
              vanId={selected}
              onClose={() => setSelected(null)}
            />
          ) : null}
        </div>
      </UnderlineTabPanel>
    </UnderlineTabs>
  );
}

function TemplateCatalogue() {
  const trpc = useTRPC(),
    session = useSession(),
    [offset, setOffset] = useState(0),
    [selected, setSelected] = useState<string | null | undefined>(undefined);
  const q = useQuery(trpc.restocks.templates.queryOptions({ offset })),
    state = shownState(q, q.data);
  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-heading text-lg font-semibold">Templates</h2>
        <Button className="min-h-11" onClick={() => setSelected(null)}>
          New template
        </Button>
      </div>
      {state === 'loading' ? (
        <Skeleton className="h-40" />
      ) : state === 'offline' ? (
        <OfflineState icon={IconInventory} what="templates" />
      ) : state === 'error' ? (
        <ErrorState
          icon={IconInventory}
          what="templates"
          onRetry={() => void q.refetch()}
        />
      ) : (
        <>
          <StaleNotice show={refreshFailed(q, q.data)} onRetry={() => void q.refetch()} />
          {!q.data?.rows.length ? (
            <p className="text-sm text-muted-foreground">
              No templates yet. Create one from blank, another template or a van's
              effective policy.
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {q.data.rows.map((t) => (
                <li key={t.templateId}>
                  <button
                    className="min-h-11 w-full space-y-1 py-3 text-left hover:bg-surface-2"
                    onClick={() => setSelected(t.templateId)}
                  >
                    <span className="block break-words text-sm font-semibold">
                      {t.name}
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      {t.partCount} parts · {t.followerCount} following vans
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {q.data && q.data.total > 25 ? (
            <div className="flex flex-wrap gap-2">
              <Button
                variant="ghost"
                disabled={offset === 0 || q.isFetching}
                onClick={() => setOffset((n) => Math.max(0, n - 25))}
              >
                Previous templates
              </Button>
              <Button
                variant="ghost"
                disabled={q.data.nextOffset === null || q.isFetching}
                onClick={() => setOffset(q.data!.nextOffset!)}
              >
                Next templates
              </Button>
            </div>
          ) : null}
        </>
      )}
      {selected !== undefined ? (
        <VanTemplateSheet
          key={`${session.orgId}:${session.userId}:${selected ?? 'new'}`}
          templateId={selected ?? undefined}
          onClose={() => {
            setSelected(undefined);
            void q.refetch();
          }}
        />
      ) : null}
    </section>
  );
}
